import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  signInWithCustomToken,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase/config";
import { STORAGE_KEYS, APP_CONFIG } from "../config/appConfig";
import {
  isInRnWebView,
  requestNativeSocialSignIn,
  requestNativeSignOut,
} from "../bridge/webviewBridge";

const googleProvider = new GoogleAuthProvider();

// --- 소셜 로그인 ---
export async function signInWithSocial({ provider, keepLogin = false }) {
  _socialSigningIn = true;
  let userCredential;

  try {
  if (isInRnWebView()) {
    const result = await requestNativeSocialSignIn(provider);
    if (provider === "google") {
      try {
        const credential = GoogleAuthProvider.credential(result.idToken);
        userCredential = await signInWithCredential(auth, credential);
      } catch (err) {
        // 디버그: localStorage에 에러 기록
        try { localStorage.setItem('__googleSigninError', JSON.stringify({ code: err.code, message: err.message, ts: Date.now() })); } catch {}
        throw err;
      }
    } else if (provider === "kakao") {
      const customToken = await getKakaoCustomToken(result.accessToken);
      userCredential = await signInWithCustomToken(auth, customToken);
    } else if (provider === "apple") {
      const customToken = await getAppleCustomToken(result.identityToken, result.displayName);
      userCredential = await signInWithCustomToken(auth, customToken);
    }
  } else {
    if (provider === "google") {
      userCredential = await signInWithPopup(auth, googleProvider);
    } else if (provider === "kakao") {
      throw new Error("카카오 로그인은 앱에서만 가능합니다.");
    } else if (provider === "apple") {
      throw new Error("Apple 로그인은 앱에서만 가능합니다.");
    }
  }

  const { uid, email } = userCredential.user;

  // 마지막 소셜 provider 저장 (LinkPhonePage에서 사용)
  saveLastSocialProvider(provider);

  await ensureUserDoc({ uid, email, provider });
  if (keepLogin) saveSession(uid);
  return userCredential.user;
  } finally {
    _socialSigningIn = false;
  }
}

// --- 카카오 Custom Token ---
async function getKakaoCustomToken(accessToken) {
  const cfUrl = APP_CONFIG.kakaoCustomTokenUrl;
  if (!cfUrl) throw new Error("카카오 로그인 서비스가 설정되지 않았습니다.");

  const res = await fetch(cfUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ accessToken }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "카카오 로그인 실패");
  return data.customToken;
}

// --- Apple Custom Token ---
async function getAppleCustomToken(identityToken, displayName) {
  const cfUrl = APP_CONFIG.appleCustomTokenUrl;
  if (!cfUrl) throw new Error("Apple 로그인 서비스가 설정되지 않았습니다.");

  const res = await fetch(cfUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identityToken, displayName: displayName || "" }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Apple 로그인 실패");
  return data.customToken;
}

// --- 이메일 로그인 ---
export async function signInWithEmailPassword({ email, password, skipEnsure = false }) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  const { uid } = cred.user;
  if (!skipEnsure) {
    await ensureUserDoc({ uid, email, provider: "email" });
  }
  saveSession(uid);
  return cred.user;
}

// --- 이메일 회원가입 ---
export async function signUpWithEmailPassword({ email, password }) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  return cred.user;
}

// --- Auth 상태 감시 ---
export function watchAuthState(onChange) {
  return onAuthStateChanged(auth, onChange);
}

// --- 로그아웃 ---
export async function signOutUser() {
  if (isInRnWebView()) requestNativeSignOut();
  clearSession();
  await signOut(auth);
}

// --- users/{uid} upsert (개선: 이미 연결된 계정 있으면 스킵) ---
export async function ensureUserDoc({ uid, email, provider }) {
  // linkedSocialUid로 이미 연결된 계정 있는지 확인
  const q1 = query(collection(db, "users"), where("linkedSocialUid", "==", uid));
  const q1Snap = await getDocs(q1);
  if (!q1Snap.empty) return; // 이미 기존 계정에 연결됨 → 스킵

  // linkedEmailUid로 이미 연결된 계정 있는지 확인
  const q2 = query(collection(db, "users"), where("linkedEmailUid", "==", uid));
  const q2Snap = await getDocs(q2);
  if (!q2Snap.empty) return; // 이미 기존 계정에 연결됨 → 스킵

  const ref = doc(db, "users", uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    // 문서 없으면 빈 문서 만들지 않음 — LinkPhonePage에서 initUserDoc() 처리
    return;
  }

  // 기존 문서에 누락된 필드 보강
  const data = snap.data();
  const updates = {};
  if (provider && !data.provider) updates.provider = provider;
  if (data.phoneE164 === undefined) updates.phoneE164 = "";
  if (data.phoneVerified === undefined) updates.phoneVerified = false;
  if (Object.keys(updates).length > 0) {
    updates.updatedAt = serverTimestamp();
    await setDoc(ref, updates, { merge: true });
  }
}

// --- 세션 ---
export function saveSession(uid) {
  try {
    sessionStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify({ uid }));
  } catch { /* noop */ }
}

export function clearSession() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.SESSION);
    sessionStorage.removeItem("lastSocialProvider");
  } catch { /* noop */ }
}

// --- 소셜 로그인 처리 중 플래그 (AuthContext 중복 방지) ---
let _socialSigningIn = false;
export function isSocialSigningIn() { return _socialSigningIn; }

// --- 마지막 소셜 로그인 provider ---
export function saveLastSocialProvider(provider) {
  try {
    sessionStorage.setItem("lastSocialProvider", provider);
  } catch { /* noop */ }
}

export function getLastSocialProvider() {
  try {
    return sessionStorage.getItem("lastSocialProvider") || null;
  } catch {
    return null;
  }
}
